import React, { useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { MoreHorizontal } from "react-feather";

const DROPDOWN_WIDTH = 188;
const DROPDOWN_OFFSET = 6;

const ActionDropdown = ({ isOpen, onToggle, onClose, actions = [] }) => {
  const triggerRef  = useRef(null);
  const dropdownRef = useRef(null);

  /* Apply position directly to the dropdown DOM node — no state, no re-render */
  const applyPosition = useCallback(() => {
    if (!triggerRef.current || !dropdownRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const vw   = window.innerWidth;

    let left = rect.right - DROPDOWN_WIDTH;
    if (left < 8) left = rect.left;
    if (left + DROPDOWN_WIDTH > vw - 8) left = vw - DROPDOWN_WIDTH - 8;

    const estimatedHeight = actions.length * 44 + 12;
    let top = rect.bottom + DROPDOWN_OFFSET;
    if (rect.bottom + estimatedHeight > window.innerHeight - 8) {
      top = rect.top - estimatedHeight - DROPDOWN_OFFSET;
    }

    dropdownRef.current.style.top  = `${top + window.scrollY}px`;
    dropdownRef.current.style.left = `${left + window.scrollX}px`;
  }, [actions.length]);

  /* Reposition on scroll / resize */
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("scroll", applyPosition, true);
    window.addEventListener("resize", applyPosition);
    return () => {
      window.removeEventListener("scroll", applyPosition, true);
      window.removeEventListener("resize", applyPosition);
    };
  }, [isOpen, applyPosition]);

  /* Close on outside click */
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => {
      if (
        triggerRef.current  && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) onClose?.();
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [isOpen, onClose]);

  const dropdown = isOpen ? ReactDOM.createPortal(
    <div
      ref={(el) => {
        dropdownRef.current = el;
        /* Position immediately when the element mounts into the DOM */
        if (el) {
          applyPosition();
          el.style.visibility = "visible";
        }
      }}
      className="animate-scale-in"
      style={{
        position:      "absolute",
        top:           0,          /* overwritten by applyPosition before paint */
        left:          0,
        zIndex:        99999,
        width:         DROPDOWN_WIDTH,
        background:    "var(--dropdown-bg)",
        border:        "1px solid var(--dropdown-border)",
        borderRadius:  12,
        boxShadow:     "0 16px 48px rgba(0,0,0,0.3)",
        backdropFilter:"blur(20px)",
        padding:       6,
        visibility:    "hidden",   /* hidden until applyPosition runs */
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {actions.map(({ label, icon: Icon, onClick, color, disabled }, i) => (
        <button
          key={i}
          type="button"
          disabled={disabled}
          onClick={(e) => { 
            e.stopPropagation(); 
            if (!disabled && onClick) {
              onClick();
            }
          }}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            width: "100%", padding: "8px 12px", borderRadius: 8,
            background: "transparent", border: "none",
            color:      disabled ? "var(--text-muted)" : (color || "var(--text-primary)"),
            fontSize:   "0.8125rem", fontWeight: 500,
            cursor:     disabled ? "not-allowed" : "pointer",
            opacity:    disabled ? 0.45 : 1,
            transition: "background 0.12s",
            textAlign:  "left",
          }}
          onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = "var(--nav-hover)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
          {Icon && (
            <div style={{
              width: 26, height: 26, borderRadius: 7, flexShrink: 0,
              background: disabled ? "var(--icon-btn-bg)" : `${color || "#6366f1"}15`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Icon size={13} strokeWidth={2} style={{ color: disabled ? "var(--text-muted)" : (color || "#6366f1") }} />
            </div>
          )}
          {label}
        </button>
      ))}
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
        <button
          ref={triggerRef}
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggle(e); }}
          style={{
            width: 30, height: 30, borderRadius: 8,
            border:     `1px solid ${isOpen ? "rgba(99,102,241,0.3)" : "var(--glass-border)"}`,
            background: isOpen ? "rgba(99,102,241,0.1)" : "var(--icon-btn-bg)",
            color:      isOpen ? "var(--brand-indigo)" : "var(--text-secondary)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", transition: "all 0.15s",
          }}
          onMouseEnter={(e) => { if (!isOpen) { e.currentTarget.style.background = "rgba(99,102,241,0.08)"; e.currentTarget.style.borderColor = "rgba(99,102,241,0.2)"; e.currentTarget.style.color = "var(--brand-indigo)"; } }}
          onMouseLeave={(e) => { if (!isOpen) { e.currentTarget.style.background = "var(--icon-btn-bg)"; e.currentTarget.style.borderColor = "var(--glass-border)"; e.currentTarget.style.color = "var(--text-secondary)"; } }}
        >
          <MoreHorizontal size={14} strokeWidth={2} />
        </button>
      </div>
      {dropdown}
    </>
  );
};

export default ActionDropdown;
